import React from 'react'
import { Star, MapPin, Clock, Phone, Heart, Share2 } from 'lucide-react'
import { Card } from './Card'
import { Badge } from './Badge'
import { Button } from './Button'

export interface BusinessCardProps {
  /**
   * Business ID
   */
  id: string
  /**
   * Business name
   */
  name: string
  /**
   * Business description
   */
  description: string
  /**
   * Business category
   */
  category: string
  /**
   * Business rating
   */
  rating: number
  /**
   * Number of reviews
   */
  reviewCount: number
  /**
   * Business location
   */
  location: string
  /**
   * Business image URL
   */
  image: string
  /**
   * Business features/tags
   */
  features?: string[]
  /**
   * Business hours
   */
  hours?: string
  /**
   * Business phone
   */
  phone?: string
  /**
   * Is business verified
   */
  verified?: boolean
  /**
   * Is business featured
   */
  featured?: boolean
  /**
   * Is business favorited
   */
  favorited?: boolean
  /**
   * On favorite toggle
   */
  onFavorite?: (id: string) => void
  /**
   * On share
   */
  onShare?: (id: string) => void
  /**
   * On click
   */
  onClick?: (id: string) => void
  /**
   * Custom className
   */
  className?: string
}

const BusinessCard = React.forwardRef<HTMLDivElement, BusinessCardProps>(
  (
    {
      id,
      name,
      description,
      category,
      rating,
      reviewCount,
      location,
      image,
      features = [],
      hours,
      phone,
      verified = false,
      featured = false,
      favorited = false,
      onFavorite,
      onShare,
      onClick,
      className = '',
      ...props
    },
    ref
  ) => {
    const handleCardClick = () => {
      onClick?.(id)
    }

    const handleFavoriteClick = (e: React.MouseEvent) => {
      e.stopPropagation()
      onFavorite?.(id)
    }

    const handleShareClick = (e: React.MouseEvent) => {
      e.stopPropagation()
      onShare?.(id)
    }

    return (
      <Card
        ref={ref}
        clickable
        hover
        className={`group cursor-pointer ${className}`}
        onClick={handleCardClick}
        {...props}
      >
        {/* Image Section */}
        <div className="relative h-48 sm:h-56 lg:h-64 overflow-hidden rounded-t-lg">
          <img
            src={image}
            alt={name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
          
          {/* Overlay Badges */}
          <div className="absolute top-3 left-3 flex flex-col gap-2">
            {featured && (
              <Badge variant="warning" size="sm">
                מומלץ
              </Badge>
            )}
            {verified && (
              <Badge variant="success" size="sm">
                מאומת
              </Badge>
            )}
          </div>

          {/* Action Buttons */}
          <div className="absolute top-3 right-3 flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleFavoriteClick}
              className="bg-white/90 hover:bg-white"
            >
              <Heart 
                className={`h-4 w-4 ${
                  favorited ? 'fill-red-500 text-red-500' : 'text-gray-600'
                }`} 
              />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleShareClick}
              className="bg-white/90 hover:bg-white"
            >
              <Share2 className="h-4 w-4 text-gray-600" />
            </Button>
          </div>
        </div>

        {/* Content Section */}
        <div className="p-4 sm:p-6">
          {/* Header */}
          <div className="mb-3">
            <div className="flex items-start justify-between mb-2">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 line-clamp-1">
                {name}
              </h3>
              <div className="flex items-center gap-1 ml-2">
                <Star className="h-4 w-4 text-yellow-400 fill-current" />
                <span className="text-sm font-medium text-gray-700">
                  {rating.toFixed(1)}
                </span>
                <span className="text-sm text-gray-500">
                  ({reviewCount})
                </span>
              </div>
            </div>
            
            <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
              <MapPin className="h-4 w-4 flex-shrink-0" />
              <span className="line-clamp-1">{location}</span>
            </div>
          </div>

          {/* Description */}
          <p className="text-gray-600 text-sm sm:text-base line-clamp-2 mb-4">
            {description}
          </p>

          {/* Features */}
          {features.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {features.slice(0, 3).map((feature, index) => (
                <Badge key={index} variant="secondary" size="sm">
                  {feature}
                </Badge>
              ))}
              {features.length > 3 && (
                <Badge variant="secondary" size="sm">
                  +{features.length - 3} עוד
                </Badge>
              )}
            </div>
          )}

          {/* Footer Info */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm text-gray-500">
            <div className="flex items-center gap-4">
              {hours && (
                <div className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  <span>{hours}</span>
                </div>
              )}
              {phone && (
                <div className="flex items-center gap-1">
                  <Phone className="h-4 w-4" />
                  <span>{phone}</span>
                </div>
              )}
            </div>
            
            <div className="text-primary-600 font-medium">
              {category}
            </div>
          </div>
        </div>
      </Card>
    )
  }
)

BusinessCard.displayName = 'BusinessCard'

export { BusinessCard }
